import React, { useEffect, useState } from "react";
import { Sparkles, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import api from "../../lib/api";
import { PageHeader, Card, Stat, RedButton } from "../../components/crm/ui";
import { toast } from "sonner";

const RED = "#FF0000";
const COLORS = ["#FF0000", "#111827", "#64748B", "#F59E0B", "#10B981", "#3B82F6"];

export default function Reports() {
  const [a, setA] = useState(null);
  const [brief, setBrief] = useState(null);
  const [loadingBrief, setLoadingBrief] = useState(false);

  useEffect(() => {
    api.get("/reports/analytics").then(({ data }) => setA(data)).catch(() => {});
    api.get("/weekly-briefs").then(({ data }) => { if (data.items[0]) setBrief(data.items[0]); }).catch(() => {});
  }, []);

  const generate = async () => { setLoadingBrief(true); try { const { data } = await api.post("/ai/weekly-brief"); setBrief(data); toast.success("Weekly brief generated"); } catch { toast.error("Could not generate"); } finally { setLoadingBrief(false); } };

  if (!a) return <div className="text-slate-400 text-sm">Loading analytics…</div>;

  return (
    <div>
      <PageHeader title="Reports & Analytics" subtitle="Operational metrics — attribution, follow-up and conversion.">
        <RedButton onClick={generate} disabled={loadingBrief} data-testid="generate-brief"><Sparkles size={15} /> {loadingBrief ? "Generating…" : "Generate Weekly Brief"}</RedButton>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Form Submissions" value={a.form_submissions} />
        <Stat label="QR Scans" value={a.qr_scans} tone="red" />
        <Stat label="Follow-Ups Done" value={a.follow_ups.completed} tone="good" />
        <Stat label="Follow-Ups Overdue" value={a.follow_ups.overdue} tone="warn" />
      </div>

      {brief && (
        <Card className="p-6 mb-6" data-testid="weekly-brief">
          <div className="flex items-center gap-2 mb-3"><FileText className="text-alto-red" size={18} /><h2 className="font-display text-lg font-bold text-slate-900">Weekly Follow-Up Brief</h2><span className="text-xs text-slate-400">{brief.generated_at?.slice(0,10)}</span></div>
          {brief.narrative && <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4"><div className="flex items-center gap-1.5 text-xs font-semibold text-alto-red mb-1.5"><Sparkles size={12} /> AI-GENERATED</div><p className="text-sm text-slate-700">{brief.narrative}</p></div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div><p className="font-display text-2xl font-bold text-slate-900">{brief.new_contacts}</p><p className="text-xs text-slate-500">New Contacts</p></div>
            <div><p className="font-display text-2xl font-bold text-emerald-600">{brief.follow_ups.completed}</p><p className="text-xs text-slate-500">Completed</p></div>
            <div><p className="font-display text-2xl font-bold text-amber-600">{brief.follow_ups.pending}</p><p className="text-xs text-slate-500">Pending</p></div>
            <div><p className="font-display text-2xl font-bold text-alto-red">{brief.follow_ups.overdue}</p><p className="text-xs text-slate-500">Overdue</p></div>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-display font-bold text-slate-900 mb-4">Contacts by Source</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={a.by_source.slice(0, 8)}><XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill={RED} radius={[4,4,0,0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <h3 className="font-display font-bold text-slate-900 mb-4">New Contacts Over Time</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={a.over_time}><XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Line type="monotone" dataKey="count" stroke={RED} strokeWidth={2} dot={{ r: 3 }} /></LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <h3 className="font-display font-bold text-slate-900 mb-4">Pipeline Distribution</h3>
          {a.by_stage.length === 0 ? <p className="text-sm text-slate-400">No data yet.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={a.by_stage} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={{ fontSize: 11 }}>{a.by_stage.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="p-6">
          <h3 className="font-display font-bold text-slate-900 mb-4">Top Campaigns</h3>
          <div className="space-y-3">
            {a.by_campaign.filter((c) => c.name !== "—").slice(0, 8).map((c) => {
              const max = Math.max(...a.by_campaign.map((x) => x.count), 1);
              return <div key={c.name}><div className="flex justify-between text-sm mb-1"><span className="text-slate-600 truncate">{c.name}</span><span className="font-semibold">{c.count}</span></div><div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-alto-black rounded-full" style={{ width: `${(c.count/max)*100}%` }} /></div></div>;
            })}
            {a.by_campaign.filter((c) => c.name !== "—").length === 0 && <p className="text-sm text-slate-400">No campaign data yet.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
